/* © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import * as Telemetry from '@prom/telemetry-browser'

export enum AccountMethod {
  MICROSOFT = 'Microsoft',
  GOOGLE = 'Google',
  APPLE = 'Apple',
  EMAIL = 'email/password',
}

export enum AccessPoint {
  CREATE = 'create account',
  SIGNUP = 'sign up',
  ACTIONS = 'required actions',
}

export const tracker = {
  // helper function
  mixpanelTrackHelper(description: string, data: any = {}) {
    Telemetry.track(description, data)
  },

  // triggered when user clicks sign button to start sign in process
  signInStart(method: AccountMethod) {
    this.mixpanelTrackHelper('Sign In User - Start', { Method: method })
  },
  // triggered when user submitts email and password for email sign in
  signInSubmit() {
    this.mixpanelTrackHelper('Sign In User - Submit')
  },
  createAccountStart(method: AccountMethod) {
    // triggered when user clicks the button to start the account creation process
    this.mixpanelTrackHelper('Create Account - Start', { Method: method })
  },
  createAccountCancel() {
    // triggered when user clicks the back button on the create account email screen
    this.mixpanelTrackHelper('Create Account - Cancel')
  },
  clickExistingAccountLink(page: AccessPoint) {
    // triggered when user clicks the link to go back to sign in page
    this.mixpanelTrackHelper('Click Existing Account Link', { 'Page Name': page })
  },
  saveAccountDetails(
    data: {
      optIn?: boolean
      jobTitle?: string
      languageCode?: string
      countryCode?: string
    },
    method?: AccountMethod,
  ) {
    // triggered when user submit new account or required account action details
    this.mixpanelTrackHelper('Create Account - Submit', {
      Method: method,
      'Country Code': data.countryCode,
      'Language Code': data.languageCode,
      'Job Title': data.jobTitle,
      'Market Opt-in': data.optIn,
    })
  },
  updateAccountDetails(jobTitle: string) {
    // triggered when user submit new account or required account action details
    this.mixpanelTrackHelper('Update Account', { 'Job Title': jobTitle })
  },
  verifyAccount(autoFill?: boolean) {
    // triggered when user submits a verification code to complete email/password account
    this.mixpanelTrackHelper('Create Account - Verify', { 'Auto Filled': !!autoFill })
  },
  sendNewCreateAccountCode() {
    // triggered when user submits a verification code to reset password
    this.mixpanelTrackHelper('Send New Create Account Code')
  },
  forgotPasswordStart() {
    // triggered when user clicks on "Forgot password"
    this.mixpanelTrackHelper('Forgot Password - Start')
  },
  forgotPasswordSubmit() {
    // triggered when user submits an email from forgot password screen
    this.mixpanelTrackHelper('Forgot Password - Submit')
  },
  forgotPasswordCancel() {
    // triggered when user clicks the cancel button on forgot password screen
    this.mixpanelTrackHelper('Forgot Password - Cancel')
  },
  resetPasswordSubmit() {
    // triggered when user submits an email from reset password screen
    this.mixpanelTrackHelper('Reset Password - Submit')
  },
  resetPasswordVerify(autoFill?: boolean) {
    // triggered when user submits a verification code to reset password
    this.mixpanelTrackHelper('Reset Password - Verify', { 'Auto Filled': !!autoFill })
  },
  sendNewForgotPasswordCode() {
    // triggered when user submits a verification code to reset password
    this.mixpanelTrackHelper('Send New Reset Password Code')
  },
  sendNewDeletionVerificationCode() {
    // triggered when user requests a new verification code when deleting their account
    this.mixpanelTrackHelper('Send New Deletion Verification Code')
  },
  requestAccountDeletion() {
    // triggered when user starts the deletion process by submitting an email
    this.mixpanelTrackHelper('Request Account Deletion')
  },
  requestAccountDeletionCancel() {
    // triggered when user starts the deletion process by submitting an email
    this.mixpanelTrackHelper('Request Account Deletion - Cancel')
  },
  triggerAccountDeletion(autoFill?: boolean) {
    // triggered when user starts the deletion process by entering a verification code
    this.mixpanelTrackHelper('Account Deletion - Verify', { 'Auto Filled': !!autoFill })
  },
  accountDeletionCancel() {
    // triggered when user cancels the deletion process
    this.mixpanelTrackHelper('Account Deletion - Cancel')
  },
  permanentlyDeleteAccount() {
    // triggered when user completes request to delete their account
    this.mixpanelTrackHelper('Account Deletion - Complete')
  },
  acceptEULA() {
    this.mixpanelTrackHelper('Accept EULA - Required Action')
  },

  /** Web Embers */
  viewEULA(accessPoint: AccessPoint) {
    this.mixpanelTrackHelper('View EULA', { 'Access Point': accessPoint })
  },
  getPrometheanApp(from?: string, device?: string) {
    // triggered when user clicks on link to get the Promethean app
    this.mixpanelTrackHelper('Get Promethean App', { 'Page Name': from, 'App Store': device })
  },
}

export default tracker
